import React from 'react';
import Styled from 'styled';
import { intlShape, InjectedIntl, FormattedMessage } from 'react-intl';
import { instanceOf } from 'prop-types';
import { Mutation, MutationComponentOptions } from '@apollo/client/react/components';
import { Settings, Editor as TinymceEditor } from 'tinymce';

import { Site } from 'four-nets/site';
import { ViewerFragment } from 'four-nets/user/viewer/viewer.graphql';

import uploadMutation, { UploadImageMutation, UploadImageMutationVariables } from './mutations/upload.graphql';
import defaultStyles from './editor.scss';

// imports css as string to use with tinymce
const contentStyle = require('!!raw-loader!./content-style.css') as string;

export interface Props {
    forwardedRef?: React.Ref<TinymceEditor>;
    styles: typeof defaultStyles;
    viewer: ViewerFragment;
}

export interface State {
    editor?: TinymceEditor;
    tinymce?: typeof import('tinymce');
}

const { props: errorUploadingImage } = <FormattedMessage id='ERROR_UPLOADING_IMAGE' />;

function setRef<T = any>(ref: React.Ref<T>, value: T | null) {
    if (ref instanceof Object && ref.hasOwnProperty('current')) {
        (ref as any).current = value;
    } else if (ref instanceof Function) {
        ref(value);
    }
}

class Editor extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: instanceOf(Site),
        intl: intlShape,
    };

    context!: {
        intl: InjectedIntl;
        site: Site;
    };

    state: State = {};
    ref = React.createRef<HTMLDivElement>();
    upload!: MutationComponentOptions<UploadImageMutation, UploadImageMutationVariables>;

    componentDidMount() {
        import('tinymce').then(() => {
            this.setState({ tinymce: window.tinymce });
        });
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (!prevState.editor && this.state.editor) {
            if (this.props.forwardedRef) {
                setRef(this.props.forwardedRef, this.state.editor);
            }
        }

        if (!prevState.tinymce && this.state.tinymce && this.ref.current) {
            const { tinymce } = this.state;

            (tinymce as any).baseURL = '/__tinymce';

            const allowScripts =
                (this.props.viewer && this.props.viewer.isSuperstaff) ||
                /^(pr_clanok|pr_clanek|modrastrecha_sk|modrastrecha_cz)$/.test(this.props.viewer.username);

            const settings: Settings & { mobile: Settings; image_dimensions: boolean } = {
                menubar: false,
                relative_urls: false,
                plugins: 'link image code paste',
                target: this.ref.current,
                paste_as_text: true,
                toolbar:
                    'undo redo | styleselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image | code',
                mobile: { theme: 'mobile' },
                image_dimensions: false,
                min_height: 380,
                content_style: contentStyle,
                extended_valid_elements: allowScripts ? 'script[language|type|src],style' : undefined,
                init_instance_callback: (editor: TinymceEditor) => {
                    this.setState({
                        editor,
                    });
                },
                images_upload_handler: (blobInfo, success, failure) => {
                    const fail = () => {
                        failure(this.context.intl.formatMessage(errorUploadingImage));
                    };

                    this.upload({ variables: { file: blobInfo.blob() } })
                        .then(result => {
                            const photofile = (
                                (result &&
                                    result.data &&
                                    result.data.uploadPhotoFiles &&
                                    result.data.uploadPhotoFiles.photoFiles) ||
                                []
                            ).pop();

                            if (photofile) {
                                const { editor } = this.state;

                                if (editor) {
                                    const handler = (e: any) => {
                                        if (e.format === 'html') {
                                            const el = document.createElement('div');
                                            el.innerHTML = e.content;

                                            const img = el.querySelector(`img[src="${photofile.src}"]`);

                                            if (img && photofile.srcset) {
                                                img.setAttribute('srcset', photofile.srcset);
                                            }
                                            e.content = el.innerHTML;
                                        }
                                        editor.off('BeforeSetContent', handler as any);
                                    };
                                    editor.on('BeforeSetContent', handler);
                                }
                                success(photofile.src);
                            } else {
                                fail();
                            }
                        })
                        .catch(fail);
                },
            };

            if (this.context.site.isSlovak) {
                Object.assign(settings, {
                    language: 'sk',
                });
            } else if (this.context.site.isCzech) {
                Object.assign(settings, {
                    language: 'cs',
                });
            }

            tinymce.init(settings);
        }
    }

    componentWillUnmount() {
        if (this.props.forwardedRef) {
            setRef(this.props.forwardedRef, null);
        }
    }

    render() {
        const { styles } = this.props;
        return (
            <Mutation<UploadImageMutation, UploadImageMutationVariables> mutation={uploadMutation}>
                {upload => {
                    this.upload = upload;
                    return <div className={styles.editor} ref={this.ref} />;
                }}
            </Mutation>
        );
    }
}

export default Styled(defaultStyles)(
    React.forwardRef<TinymceEditor, Props>((props, ref) => <Editor forwardedRef={ref} {...props} />)
);
